/** Cross-session unified activity feed (M0.3): every event, newest first, shown directly. */
import { formatRelative } from '../lib/format';
import { buildActivityFeed, type ActivityItem } from '../lib/sessions';
import type { SessionView } from '../lib/types';
import { EventKindBadge, SourceBadge } from './SessionBadges';
import { MaskedText } from './MaskedText';
import { Icon } from './Icon';

export function ActivityFeed({
  sessions,
  now,
  onOpen,
}: {
  sessions: readonly SessionView[];
  now: number;
  onOpen(session: Pick<SessionView, 'source' | 'session_id'>): void;
}) {
  const items = buildActivityFeed(sessions);
  if (items.length === 0) {
    return (
      <section className="rounded-2xl border border-white/10 bg-[#0f1629] p-6 text-center">
        <Icon name="sessions" className="mx-auto h-8 w-8 text-slate-600" />
        <p className="mt-2 text-sm text-slate-300">没有活动</p>
        <p className="mt-1 text-xs text-slate-500">
          会话事件（消息/工具/状态/文件/进度）会在这里合并成一条时间线。
        </p>
      </section>
    );
  }
  return (
    <div className="space-y-2">
      {items.map((item: ActivityItem) => (
        <div
          key={item.key}
          role="button"
          tabIndex={0}
          onClick={() => onOpen(item)}
          onKeyDown={(event) => {
            if (event.target !== event.currentTarget) return;
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              onOpen(item);
            }
          }}
          aria-label="查看该活动所属会话"
          className="w-full cursor-pointer rounded-2xl border border-white/10 bg-[#0f1629] p-3 text-left outline-none active:bg-white/[0.04] focus-visible:border-indigo-400/60"
        >
          <div className="flex items-center justify-between gap-2 text-[10px] text-slate-500">
            <span className="flex min-w-0 flex-wrap items-center gap-1.5">
              <EventKindBadge kind={item.event.kind} />
              <SourceBadge source={item.source} />
              <span
                className="max-w-[9rem] truncate text-slate-400"
                onClick={(event) => event.stopPropagation()}
              >
                <MaskedText value={item.session_title} />
              </span>
            </span>
            <span className="shrink-0">{formatRelative(item.event.ts, now)}</span>
          </div>
          <div className="mt-1.5 text-xs text-slate-200">
            {item.event.tool ? (
              <code className="mr-1 text-sky-200/80">{item.event.tool} ·</code>
            ) : null}
            {item.event.role ? (
              <span className="mr-1 text-[10px] text-slate-500">{item.event.role} ·</span>
            ) : null}
            {item.event.text ? (
              <div className="mt-0.5" onClick={(event) => event.stopPropagation()}>
                <MaskedText value={item.event.text} multiline />
              </div>
            ) : (
              <span className="text-slate-500">（无文本事件）</span>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
